import { isEqual } from "lodash";
import { JsonPatchOperation } from "_shared/_api/attorney-hub-api-service";
import IndividualProfile from "marketplace/individuals/profiles/individual-profile";

function compareAndPush(
  payload: JsonPatchOperation[],
  updated: IndividualProfile,
  original: IndividualProfile,
  key: keyof IndividualProfile,
): void {
  if (updated[key] !== original[key]) {
    payload.push({ op: "replace", path: `/${key}`, value: updated[key] });
  }
}

function deepCompareAndPush(
  payload: JsonPatchOperation[],
  updated: IndividualProfile,
  original: IndividualProfile,
  key: keyof IndividualProfile,
): void {
  if (!isEqual(updated[key], original[key])) {
    payload.push({ op: "replace", path: `/${key}`, value: updated[key] });
  }
}

export function buildUpdateIndividualProfilePayload(
  isVisible?: boolean,
  originalProfile?: IndividualProfile,
  updatedProfile?: IndividualProfile,
): JsonPatchOperation[] {
  const payload: JsonPatchOperation[] = [];

  if (isVisible !== undefined) {
    payload.push({ op: "replace", path: "/isVisible", value: isVisible });
  }

  if (!originalProfile || !updatedProfile) {
    return payload;
  }

  compareAndPush(payload, updatedProfile, originalProfile, "firstName");
  compareAndPush(payload, updatedProfile, originalProfile, "lastName");
  compareAndPush(payload, updatedProfile, originalProfile, "professionalRegistration");
  compareAndPush(payload, updatedProfile, originalProfile, "description");
  compareAndPush(payload, updatedProfile, originalProfile, "isOnCall");
  compareAndPush(payload, updatedProfile, originalProfile, "school");
  compareAndPush(payload, updatedProfile, originalProfile, "degree");
  compareAndPush(payload, updatedProfile, originalProfile, "numberOfAllowedApplications");
  compareAndPush(payload, updatedProfile, originalProfile, "barAdmissionYear");
  compareAndPush(payload, updatedProfile, originalProfile, "potentialDiversityCandidate");
  compareAndPush(payload, updatedProfile, originalProfile, "collaborates");

  deepCompareAndPush(payload, updatedProfile, originalProfile, "phoneNumber");
  deepCompareAndPush(payload, updatedProfile, originalProfile, "location");
  deepCompareAndPush(payload, updatedProfile, originalProfile, "categories");
  deepCompareAndPush(payload, updatedProfile, originalProfile, "regionsRegistered");
  deepCompareAndPush(payload, updatedProfile, originalProfile, "fluentLanguages");
  deepCompareAndPush(payload, updatedProfile, originalProfile, "technicalLanguages");
  deepCompareAndPush(payload, updatedProfile, originalProfile, "regionsLicensed");
  deepCompareAndPush(payload, updatedProfile, originalProfile, "sampleApplications");
  deepCompareAndPush(payload, updatedProfile, originalProfile, "professionalPublications");
  deepCompareAndPush(payload, updatedProfile, originalProfile, "links");

  if (!updatedProfile.email?.isEqualTo(originalProfile.email)) {
    payload.push({ op: "replace", path: "/email", value: updatedProfile.email?.value ?? "" });
  }

  if (updatedProfile.numberOfApplicationsDraftedProsecuted !== originalProfile.numberOfApplicationsDraftedProsecuted) {
    payload.push({
      op: "replace",
      path: "/numberOfApplicationsDraftedProsecuted",
      value: updatedProfile.numberOfApplicationsDraftedProsecuted,
    });
  }

  if (updatedProfile.technicalBackground !== originalProfile.technicalBackground) {
    payload.push({ op: "replace", path: "/technicalBackground", value: updatedProfile.technicalBackground?.value });
  }

  if (updatedProfile.insured?.insuredAmount !== originalProfile.insured?.insuredAmount) {
    payload.push({ op: "replace", path: "/insuredAmount", value: updatedProfile.insured?.insuredAmount });
  }

  if (updatedProfile.billingRate?.billingRate !== originalProfile.billingRate?.billingRate) {
    payload.push({ op: "replace", path: "/billingRate", value: updatedProfile.billingRate?.billingRate });
  }

  if (updatedProfile.billingRate?.billingUnit !== originalProfile.billingRate?.billingUnit) {
    payload.push({ op: "replace", path: "/billingUnit", value: updatedProfile.billingRate?.billingUnit });
  }

  return payload;
}
